import React, { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import AppLayout from './components/AppLayout';
import LoginPage from './pages/LoginPage';
import VerifyMagicLinkPage from './pages/VerifyMagicLinkPage';
import CompleteProfilePage from './pages/CompleteProfilePage';
import SearchPage from './pages/SearchPage';
import CandidateDetail from './pages/CandidateDetail';
import AdminConsolePage from './pages/AdminConsolePage';
// import MaintenancePage from './pages/MaintenancePage'; // temporary maintenance mode
import SharedListPage from './pages/SharedListPage';
import { TenantProvider, useTenant } from './context/TenantContext';
import { useCurrentUser } from './hooks/useCurrentUser';
import { getAdminTenants } from './services/api';

const TOKEN_STORAGE_KEY = 'auth_token';

/** Admin console + tenant switcher (backend roles admin / superadmin). */
function hasAdminConsoleAccess(role: string | undefined): boolean {
  return role === 'admin' || role === 'superadmin';
}

/** Scroll window to top on route change, but skip for SPA navigation. */
function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPathnameRef = useRef<string | null>(null);
  
  useEffect(() => {
    if (typeof window.history !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    const prevPathname = prevPathnameRef.current;
    prevPathnameRef.current = pathname;
    
    // Don't scroll to top when opening candidate detail from main page
    if (pathname.startsWith('/candidate/') && prevPathname === '/') {
      return;
    }
    
    // Don't scroll to top when closing candidate detail (going back to main)
    if (pathname === '/' && prevPathname?.startsWith('/candidate/')) {
      return;
    }
    
    // For all other navigations (login, fresh page load, etc.), scroll to top
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

type RequireAuthProps = { children: React.ReactElement };

function RequireAuth({ children }: RequireAuthProps): React.ReactElement {
  const location = useLocation();
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);

  if (!token) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={redirect ? `/login?redirect=${redirect}` : '/login'} replace />;
  }

  try {
    const raw = localStorage.getItem('current_user');
    const user = raw ? JSON.parse(raw) : null;
    if (user && !user.firstName) {
      const redirect = encodeURIComponent(location.pathname + location.search);
      return <Navigate to={`/complete-profile?redirect=${redirect}`} replace />;
    }
  } catch {
    // ignore malformed data
  }

  return children;
}

function TenantInitializer({ children }: { children: React.ReactNode }) {
  const { role, email } = useCurrentUser();
  const { setIsAdmin, setTenants, switchTenant } = useTenant();
  const initializedRef = useRef(false);
  const [ready, setReady] = useState(() => !hasAdminConsoleAccess(role));

  useEffect(() => {
    const isStaffAdmin = hasAdminConsoleAccess(role);
    setIsAdmin(isStaffAdmin);
    if (!isStaffAdmin) {
      setReady(true);
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const urlTenantId = params.get('tid') || localStorage.getItem('tid');

    getAdminTenants()
      .then((fetched) => {
        const ownDomain = email?.split('@')[1]?.toLowerCase();
        const sorted = ownDomain
          ? [...fetched].sort((a, b) =>
              a.domain === ownDomain ? -1 : b.domain === ownDomain ? 1 : 0,
            )
          : fetched;
        setTenants(sorted);
        const urlTid = urlTenantId ? Number(urlTenantId) : null;
        const validTid = urlTid && sorted.some(t => t.id === urlTid) ? urlTid : null;
        if (validTid) {
          switchTenant(validTid);
        } else {
          switchTenant(sorted[0]?.id ?? null);
        }
      })
      .catch(() => setTenants([]))
      .finally(() => setReady(true));
  }, [role, email, setIsAdmin, setTenants, switchTenant]);

  if (!ready) return null;

  return <>{children}</>;
}

function AuthenticatedRoutes() {
  const { selectedTenantId, isAdmin } = useTenant();

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/" element={<SearchPage key={selectedTenantId ?? 'home'} />} />
      </Route>
      <Route path="/candidate/:id" element={<CandidateDetail />} />
      <Route path="/list/:shareId" element={<SharedListPage />} />
      {isAdmin && (
        // <Route path="/admin/*" element={<MaintenancePage />} />
        <Route path="/admin/*" element={<AdminConsolePage />} />
      )}
    </Routes>
  );
}

function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/verify" element={<VerifyMagicLinkPage />} />
        <Route path="/complete-profile" element={<CompleteProfilePage />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <TenantProvider>
                <TenantInitializer>
                  <AuthenticatedRoutes />
                </TenantInitializer>
              </TenantProvider>
            </RequireAuth>
          }
        />
      </Routes>
    </>
  );
}

export default App;
